import React, { useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import ScreenShareIcon from "@mui/icons-material/ScreenShare";
import StopScreenShareIcon from "@mui/icons-material/StopScreenShare";
import { MeetingAttendee } from "../../api/video_platform";

interface AttendeeCapabilityControlsProps {
  attendee: MeetingAttendee;
  meetingId: string;
  onUpdateCapability: (
    attendeeId: string,
    capability: "audio" | "video" | "content",
    value: string,
    lock: boolean
  ) => Promise<void>;
  disabled?: boolean;
}

const AttendeeCapabilityControls: React.FC<AttendeeCapabilityControlsProps> = ({
  attendee,
  meetingId,
  onUpdateCapability,
  disabled = false,
}) => {
  const [loading, setLoading] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedCapability, setSelectedCapability] = useState<"audio" | "video" | "content" | null>(null);

  // Check if a specific capability is locked (blocked by admin)
  const isCapabilityLocked = (capability: "audio" | "video" | "content"): boolean => {
    return attendee.capabilities?.[capability]?.isLocked ?? false;
  };

  // Color logic: Red = locked (admin blocked), Gray = unlocked (user controls)
  const getIconColor = (capability: "audio" | "video" | "content"): "error" | "default" => {
    return isCapabilityLocked(capability) ? "error" : "default";
  };

  const handleCapabilityClick = (capability: "audio" | "video" | "content") => {
    setSelectedCapability(capability);
    setConfirmDialogOpen(true);
  };

  const handleConfirm = async () => {
    if (!selectedCapability) return;

    const currentlyLocked = isCapabilityLocked(selectedCapability);

    try {
      setLoading(selectedCapability);

      if (currentlyLocked) {
        // Unlock: set lock to false (user regains control)
        await onUpdateCapability(attendee.id, selectedCapability, "SendReceive", false);
      } else {
        // Lock: disable capability and lock it
        await onUpdateCapability(attendee.id, selectedCapability, "None", true);
      }
    } finally {
      setLoading(null);
      setConfirmDialogOpen(false);
      setSelectedCapability(null);
    }
  };

  const handleCancel = () => {
    setConfirmDialogOpen(false);
    setSelectedCapability(null);
  };

  const getCapabilityLabel = (capability: "audio" | "video" | "content"): string => {
    switch (capability) {
      case "audio": return "Audio";
      case "video": return "Video";
      case "content": return "Screen Share";
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {/* Audio Control */}
      <Tooltip
        title={
          isCapabilityLocked("audio")
            ? "Audio is locked - Click to unlock"
            : "Audio is unlocked - Click to lock"
        }
      >
        <span>
          <IconButton
            size="small"
            onClick={() => handleCapabilityClick("audio")}
            disabled={disabled || loading === "audio"}
            color={getIconColor("audio")}
          >
            {loading === "audio" ? (
              <CircularProgress size={20} />
            ) : isCapabilityLocked("audio") ? (
              <MicOffIcon fontSize="small" />
            ) : (
              <MicIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>

      {/* Video Control */}
      <Tooltip
        title={
          isCapabilityLocked("video")
            ? "Video is locked - Click to unlock"
            : "Video is unlocked - Click to lock"
        }
      >
        <span>
          <IconButton
            size="small"
            onClick={() => handleCapabilityClick("video")}
            disabled={disabled || loading === "video"}
            color={getIconColor("video")}
          >
            {loading === "video" ? (
              <CircularProgress size={20} />
            ) : isCapabilityLocked("video") ? (
              <VideocamOffIcon fontSize="small" />
            ) : (
              <VideocamIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>

      {/* Content Share Control */}
      <Tooltip
        title={
          isCapabilityLocked("content")
            ? "Screen share is locked - Click to unlock"
            : "Screen share is unlocked - Click to lock"
        }
      >
        <span>
          <IconButton
            size="small"
            onClick={() => handleCapabilityClick("content")}
            disabled={disabled || loading === "content"}
            color={getIconColor("content")}
          >
            {loading === "content" ? (
              <CircularProgress size={20} />
            ) : isCapabilityLocked("content") ? (
              <StopScreenShareIcon fontSize="small" />
            ) : (
              <ScreenShareIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={handleCancel}>
        <DialogTitle>
          {selectedCapability && isCapabilityLocked(selectedCapability)
            ? `Unlock ${getCapabilityLabel(selectedCapability)}?`
            : `Lock ${selectedCapability ? getCapabilityLabel(selectedCapability) : ""}?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedCapability && isCapabilityLocked(selectedCapability) ? (
              <>
                This will <strong>unlock</strong> the <strong>{getCapabilityLabel(selectedCapability)}</strong> capability for <strong>{attendee.attendeeName || attendee.externalUserId}</strong>.
                <br /><br />
                The attendee will be able to control their own {getCapabilityLabel(selectedCapability).toLowerCase()}.
              </>
            ) : (
              <>
                This will <strong>lock</strong> the <strong>{selectedCapability ? getCapabilityLabel(selectedCapability) : ""}</strong> capability for <strong>{attendee.attendeeName || attendee.externalUserId}</strong>.
                <br /><br />
                The attendee will <strong>NOT</strong> be able to change their {selectedCapability ? getCapabilityLabel(selectedCapability).toLowerCase() : ""} settings.
                Only admins will be able to control this capability.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirm} color="primary" variant="contained" autoFocus>
            {selectedCapability && isCapabilityLocked(selectedCapability) ? "Unlock" : "Lock"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AttendeeCapabilityControls;
